import React, { useState, useEffect } from 'react'
import texture from '../images/TextureBlue060C1D.png'

const ARTIST = 'Chance to Steal'
const API_KEY = process.env.REACT_APP_BANDSINTOWN_KEY

const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}

const venueOverridesByAddress = {
    '18041 Aurora Ave': "Darrell's Tavern",
    '1832 S 120th St': 'Flight Path',
    '909 SE Everett Mall Way': 'Soundbite Cider',
    '3001 Broadway': 'The Bayside Cafe',
    '5257 University Way': 'The Kraken',
    '10843 1st Ave S': 'Axe Kickers',
}

const getVenueName = (show) => {
    const apiName = show.venue?.name
    const isApiNameBad = show.lineup?.some(a => apiName?.includes(a)) || (apiName === show.title && !!show.title)
    if (!isApiNameBad) return apiName
    const address = show.venue?.street_address
    const overrideKey = Object.keys(venueOverridesByAddress).find(key => address?.startsWith(key))
    return venueOverridesByAddress[overrideKey] || address
}

const groupByYear = (shows) => {
    const groups = shows.reduce((acc, show) => {
        const year = new Date(show.datetime).getFullYear()
        if (!acc[year]) acc[year] = []
        acc[year].push(show)
        return acc
    }, {})
    return Object.entries(groups).sort((a, b) => b[0] - a[0])
}

const ShowRow = ({ show, ticketUrl }) => {
    const venueName = getVenueName(show)
    const mapsQuery = encodeURIComponent(`${show.venue?.street_address}, ${show.venue?.city}, ${show.venue?.region}`)
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`

    return (
        <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 border-b-[2px] border-b-[#4a9bad] my-2 md:my-0 lg:my-0 py-2 px-6'>
            <div className='text-3xl md:text-xl lg:text-xl'>
                <p className='px-1'>{formatDate(show.datetime)}</p>
            </div>
            <div className='text-xl md:text-xl lg:text-xl'>
                <p className='px-1 font-semibold'>{show.title || `Chance to Steal @ ${venueName}`}</p>
                <p className='px-1 text-sm text-[#cdc3b9]/70'>{show.lineup?.join(', ')}</p>
            </div>
            <div className='text-sm md:text-xl lg:text-xl leading-tight mt-2 md:mt-0'>
                <div className='px-1'>{venueName}</div>
                <a href={mapsUrl} target='_blank' rel='noreferrer' className='block px-1 text-sm text-[#cdc3b9]/70 hover:text-[#4a9bad] duration-300 leading-tight'>
                    {venueName !== show.venue?.street_address && <span className='block'>{show.venue?.street_address}</span>}
                    <span className='block'>{show.venue?.city}, {show.venue?.region}</span>
                </a>
                {ticketUrl && (
                    <div className='px-1 mt-2 md:mt-0'>
                        <a href={ticketUrl} target='_blank' rel='noreferrer'>
                            <button className='bg-[#9c34d6] rounded-md font-medium px-4 py-1 text-sm text-[#cdc3b9] hover:scale-105 hover:text-[#9c34d6] hover:bg-[#cdc3b9] hover:border-[#9c34d6] hover:border-[2px] duration-200'>Get Tickets</button>
                        </a>
                    </div>
                )}
            </div>
        </div>
    )
}

const Calendar = () => {
    const [view, setView] = useState('upcoming')
    const [shows, setShows] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    useEffect(() => {
        setLoading(true)
        setError(false)
        const dateParam = view === 'past' ? '&date=past' : '&date=upcoming'
        const url = `https://rest.bandsintown.com/artists/${encodeURIComponent(ARTIST)}/events?app_id=${API_KEY}${dateParam}`

        fetch(url)
            .then(res => {
                if (!res.ok) throw new Error('API error')
                return res.json()
            })
            .then(data => {
                setShows(Array.isArray(data) ? data : [])
                setLoading(false)
            })
            .catch(() => {
                setError(true)
                setLoading(false)
            })
    }, [view])

    const ticketUrl = (show) => show.offers?.find(o => o.type === 'Tickets')?.url

    return (
        <div className='w-full text-[#cdc3b9] py-4 px-8 pb-10' style={{ backgroundImage: `url(${texture})`, backgroundRepeat: 'repeat' }}>
            <div className='max-w-[1240px] mx-auto'>
                <h1 className='text-3xl md:text-4xl lg:text-5xl font-bebas uppercase py-4 mb-2' style={{ filter: 'url(#text-scratch)' }}>SHOWS</h1>

                <div className='flex justify-start gap-8 mb-4'>
                    <button
                        onClick={() => setView('upcoming')}
                        className={`uppercase font-bold text-base border-b-2 pb-1 transition-colors duration-200 ${view === 'upcoming' ? 'text-[#4a9bad] border-[#4a9bad]' : 'text-[#cdc3b9] border-transparent hover:text-[#4a9bad]'}`}
                    >
                        Upcoming Dates
                    </button>
                    <button
                        onClick={() => setView('past')}
                        className={`uppercase font-bold text-base border-b-2 pb-1 transition-colors duration-200 ${view === 'past' ? 'text-[#4a9bad] border-[#4a9bad]' : 'text-[#cdc3b9] border-transparent hover:text-[#4a9bad]'}`}
                    >
                        Past Dates
                    </button>
                </div>

                <div className='py-2'>
                    <div className='hidden md:grid grid-cols-3 border-b-[1px] border-b-[#4a9bad] bg-[#4a9bad] text-[#060C1D] font-extrabold rounded py-1 px-6'>
                        <div className='px-1'>Date</div>
                        <div className='px-1'>Event</div>
                        <div className='px-1'>Location</div>
                    </div>

                    {loading && (
                        <div className='flex justify-center py-10 text-[#cdc3b9] text-lg'>
                            Loading...
                        </div>
                    )}

                    {error && (
                        <div className='flex justify-center py-10 text-[#cdc3b9] text-lg'>
                            Unable to load shows. Check back soon.
                        </div>
                    )}

                    {!loading && !error && shows.length === 0 && (
                        <div className='flex justify-center py-10 text-[#cdc3b9] text-lg'>
                            {view === 'upcoming' ? 'No upcoming shows scheduled. Check back soon!' : 'No past shows found.'}
                        </div>
                    )}

                    {!loading && !error && view === 'upcoming' && shows.map((show) => (
                        <ShowRow key={show.id} show={show} ticketUrl={ticketUrl(show)} />
                    ))}

                    {!loading && !error && view === 'past' && groupByYear(shows).map(([year, yearShows]) => (
                        <div key={year}>
                            <div className='flex items-center gap-4 px-6 pt-6 pb-2'>
                                <div className='flex-1 h-px bg-[#4a9bad]/40' />
                                <span className='text-xl font-extrabold text-[#4a9bad] tracking-widest'>{year}</span>
                                <div className='flex-1 h-px bg-[#4a9bad]/40' />
                            </div>
                            {yearShows.map((show) => (
                                <ShowRow key={show.id} show={show} ticketUrl={ticketUrl(show)} />
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default Calendar
